import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, If, StyleProps } from "@hex-insights/core";
import { EditMode, Form, FormEditButtonPair, FormState, SubmitButton, useInternalField } from "@hex-insights/forms";
import {
	CalendarEventResponseCreateMutation,
	CalendarEventResponseDetailQuery,
	CalendarEventResponseUpdateMutation,
} from "../../../Utilities";
import { CalendarEventResponseFormState, CalendarEventResponseFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<CalendarEventResponseCreateMutation["createCalendarEventResponse"]>,
	"formState" | "create"
> & {
	formStateArgs?: CalendarEventResponseFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the CalendarEventResponse model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<CalendarEventResponseFormValues.Create>;
	create: CreateFunction<CalendarEventResponseFormValues.Create, T>;
	onSuccess: (formState: FormState<CalendarEventResponseFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<CalendarEventResponseFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the CalendarEventResponse model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "calendarEventResponse.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<CalendarEventResponseFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.Response formState={formState} />
			<Field.CalendarEvent.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<CalendarEventResponseUpdateMutation["updateCalendarEventResponse"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<CalendarEventResponseFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the CalendarEventResponse model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		calendarEventResponse: props.calendarEventResponse,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	calendarEventResponse: CalendarEventResponseDetailQuery["calendarEventResponse"];
	formState: FormState<CalendarEventResponseFormValues.Detail>;
	update: UpdateFunction<CalendarEventResponseFormValues.Detail, T>;
	onSuccess: (formState: FormState<CalendarEventResponseFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<CalendarEventResponseFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the CalendarEventResponse model using the given form state.
 */
export function Detail<T>({
	formState,
	calendarEventResponse,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName =
		`calendarEventResponse.detail.${calendarEventResponse.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} calendarEventResponse={calendarEventResponse} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<
		DistributiveOmit<CalendarEventResponseFormState.UseReadOnlyFormStateArgs, "initialFormValues">
	>;
};

/**
 * Read-only detail form of the CalendarEventResponse model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, calendarEventResponse: props.calendarEventResponse });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	calendarEventResponse: CalendarEventResponseDetailQuery["calendarEventResponse"];
	formState: FormState<CalendarEventResponseFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the CalendarEventResponse model using the given form state.
 */
export function ReadOnly({ formState, calendarEventResponse, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} calendarEventResponse={calendarEventResponse} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	calendarEventResponse: CalendarEventResponseDetailQuery["calendarEventResponse"];
	formState: FormState<CalendarEventResponseFormValues.Detail>;
};

function DetailFields({ formState, calendarEventResponse }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.Response formState={formState} />
			<Field.Author.Select formState={formState} currentInstance={calendarEventResponse.author} />
			<Field.CalendarEvent.Select formState={formState} currentInstance={calendarEventResponse.calendarEvent} />
		</React.Fragment>
	);
}

export type ControlledCreateOnExistingCalendarEventProps = Omit<
	CreateOnExistingCalendarEventProps<CalendarEventResponseCreateMutation["createCalendarEventResponse"]>,
	"formState" | "create"
> & {
	formStateArgs?: CalendarEventResponseFormState.UseCreateFormStateArgs;
};

export function ControlledCreateOnExistingCalendarEvent({
	formStateArgs,
	...props
}: ControlledCreateOnExistingCalendarEventProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <CreateOnExistingCalendarEvent formState={formState} create={create} {...props} />;
}

type CreateOnExistingCalendarEventProps<T> = CreateProps<T> & {
	autoSave?: boolean;
};

export function CreateOnExistingCalendarEvent<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	autoSave,
	...props
}: CreateOnExistingCalendarEventProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const {
		formValues: { response },
		onSubmitWrapper,
	} = formState;
	React.useEffect(() => {
		if (!(autoSave && response)) {
			return;
		}
		onSubmitWrapper(onSubmit)({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>);
	}, [autoSave, response, onSubmitWrapper, onSubmit]);

	const formName = "calendarEventResponse.create" + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "calendarEventID");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<Field.Response formState={formState} noHeader style={{ width: "fit-content" }} />

				<If condition={!autoSave}>
					<Form.ActionBar>
						<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
							Submit
						</SubmitButton>
					</Form.ActionBar>
				</If>
			</Form.Column>
		</Form>
	);
}

export type ControlledDetailOnExistingCalendarEventProps = Omit<
	DetailOnExistingCalendarEventProps<CalendarEventResponseUpdateMutation["updateCalendarEventResponse"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<CalendarEventResponseFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

export function ControlledDetailOnExistingCalendarEvent({
	formStateArgs,
	...props
}: ControlledDetailOnExistingCalendarEventProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		calendarEventResponse: props.calendarEventResponse,
	});

	return <DetailOnExistingCalendarEvent formState={formState} update={update} {...props} />;
}

type DetailOnExistingCalendarEventProps<T> = DetailProps<T> & {
	autoSave?: boolean;
};

export function DetailOnExistingCalendarEvent<T>({
	formState,
	calendarEventResponse,
	update,
	onSuccess,
	formNameSuffix,
	autoSave,
	...props
}: DetailOnExistingCalendarEventProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const {
		formValues: { response },
		formChanged: { response: wasResponseChanged },
		onSubmitWrapper,
	} = formState;
	React.useEffect(() => {
		if (!(autoSave && response && wasResponseChanged)) {
			return;
		}
		onSubmitWrapper(onSubmit)({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>);
	}, [autoSave, response, wasResponseChanged, onSubmitWrapper, onSubmit]);

	const formName =
		`calendarEventResponse.detail.${calendarEventResponse.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "calendarEventID");
	useInternalField(formState, "authorID");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<If condition={EditMode.isReadWrite(formState.editMode)}>
					<FormEditButtonPair formState={formState} />
				</If>

				<Field.Response formState={formState} noHeader style={{ width: "fit-content" }} />
			</Form.Column>
		</Form>
	);
}
